import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const collections = [
  { label: 'Antiques & Vintage', href: '/collections/antiques' },
  { label: 'Furniture', href: '/collections/furniture' },
  { label: 'Gifts & Home Décor', href: '/collections/gifts' },
  { label: 'Boutique Apparel', href: '/collections/apparel' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCollOpen, setMobileCollOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(false);
  }, [location]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const isActive = (href: string) => location.pathname === href;
  const isCollectionActive = location.pathname.startsWith('/collections');

  const linkClass = (active: boolean) =>
    `text-xs font-sans font-500 tracking-[0.12em] uppercase transition-colors duration-200 whitespace-nowrap cursor-pointer ${
      scrolled
        ? active
          ? 'text-antique'
          : 'text-inkblack hover:text-antique'
        : active
        ? 'text-antique'
        : 'text-white hover:text-antique'
    }`;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
        scrolled
          ? 'bg-ivory border-b border-mist'
          : 'bg-transparent'
      }`}
    >
      <nav className="px-8 lg:px-12 py-5 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center cursor-pointer flex-shrink-0">
          {scrolled ? (
            <img
              src="https://yarnellemporium.com/wp-content/uploads/2020/04/YE_Logo450.png"
              alt="Yarnell Emporium"
              className="h-14 w-auto object-contain"
            />
          ) : (
            <div className="flex flex-col">
              <span className="font-serif text-white text-xl font-600 leading-tight tracking-wide">Yarnell</span>
              <span className="font-serif text-antique text-sm font-400 leading-tight tracking-[0.2em] uppercase">Emporium</span>
            </div>
          )}
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-10">
          <Link to="/" className={linkClass(isActive('/'))}>Home</Link>

          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`flex items-center gap-1.5 ${linkClass(isCollectionActive)}`}
            >
              Collections
              <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            {dropdownOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-4 bg-ivory border border-mist py-3 min-w-56">
                {collections.map((c) => (
                  <Link
                    key={c.href}
                    to={c.href}
                    className="block px-6 py-3 text-xs font-sans font-500 tracking-[0.1em] uppercase text-inkblack hover:text-antique hover:bg-mist transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {c.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link to="/about" className={linkClass(isActive('/about'))}>About</Link>
          <Link to="/location" className={linkClass(isActive('/location'))}>Location</Link>
        </div>

        {/* Right Actions */}
        <div className="hidden lg:flex items-center gap-6">
          <a
            href="tel:9284279593"
            className={`flex items-center gap-2 text-xs font-sans tracking-wide whitespace-nowrap transition-colors cursor-pointer ${
              scrolled ? 'text-pewter hover:text-antique' : 'text-white/80 hover:text-white'
            }`}
          >
            <i className="ri-phone-line" />
            928.427-9593
          </a>
          <a
            href="https://maps.google.com/?q=Yarnell+Emporium+22899+S+Hwy+89+Yarnell+AZ"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-antique text-inkblack text-xs font-sans font-600 tracking-[0.12em] uppercase px-6 py-3 whitespace-nowrap hover:bg-bark hover:text-white transition-colors duration-200 cursor-pointer"
          >
            Visit the Store
          </a>
        </div>

        {/* Mobile Hamburger */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`lg:hidden w-8 h-8 flex items-center justify-center cursor-pointer ${
            scrolled ? 'text-inkblack' : 'text-white'
          }`}
        >
          <i className={`text-2xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'}`} />
        </button>
      </nav>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-ivory border-t border-mist px-8 py-6 flex flex-col gap-1">
          <Link to="/" className="py-4 text-xs font-sans font-500 tracking-[0.12em] uppercase text-inkblack hover:text-antique cursor-pointer border-b border-mist/60">
            Home
          </Link>
          <button
            onClick={() => setMobileCollOpen(!mobileCollOpen)}
            className="py-4 text-xs font-sans font-500 tracking-[0.12em] uppercase text-inkblack hover:text-antique cursor-pointer border-b border-mist/60 flex items-center justify-between w-full"
          >
            Collections
            <i className={`ri-arrow-down-s-line text-lg transition-transform ${mobileCollOpen ? 'rotate-180' : ''}`} />
          </button>
          {mobileCollOpen && (
            <div className="pl-4 flex flex-col">
              {collections.map((c) => (
                <Link
                  key={c.href}
                  to={c.href}
                  className="py-3 text-xs font-sans font-500 tracking-[0.1em] uppercase text-pewter hover:text-antique cursor-pointer"
                >
                  {c.label}
                </Link>
              ))}
            </div>
          )}
          <Link to="/about" className="py-4 text-xs font-sans font-500 tracking-[0.12em] uppercase text-inkblack hover:text-antique cursor-pointer border-b border-mist/60">
            About
          </Link>
          <Link to="/location" className="py-4 text-xs font-sans font-500 tracking-[0.12em] uppercase text-inkblack hover:text-antique cursor-pointer border-b border-mist/60">
            Location
          </Link>
          <div className="pt-5 flex flex-col gap-3">
            <a href="tel:9284279593" className="flex items-center gap-2 text-xs font-sans text-pewter hover:text-antique cursor-pointer">
              <i className="ri-phone-line" /> 928.427-9593
            </a>
            <a
              href="https://maps.google.com/?q=Yarnell+Emporium+Yarnell+AZ"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-antique text-inkblack text-xs font-sans font-600 tracking-[0.12em] uppercase px-6 py-3 text-center whitespace-nowrap hover:bg-bark hover:text-white transition-colors cursor-pointer"
            >
              Visit the Store
            </a>
          </div>
        </div>
      )}
    </header>
  );
}